import React, {useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {goBack} from '../../../../app-store/AccountSetupSlice'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {CALL_TYPES} from '../../../../utils/scheduling-call-rules'
import {PTArrowLeftSVG} from '../../../../assets'
import {Button} from '../../../shared/Button'
import Modal from '../../../shared/Modal'

// Flattens useScheduledAppointments' appointmentsByLocationId
// ({[locationId]: {[callKey]: row}}) into one entry per booked call, with the
// call type (for its cancel endpoint) and the location's country (DELETE body).
function getBookedAppointments({
  appointmentsByLocationId,
  locations,
  accountInfo,
}) {
  return Object.entries(appointmentsByLocationId ?? {}).flatMap(
    ([locationId, byCall]) =>
      Object.entries(byCall ?? {}).map(([callKey, appointment]) => ({
        appointment,
        callType: Object.values(CALL_TYPES).find(ct => ct.key === callKey),
        country:
          locations?.find(loc => loc.locationId === locationId)?.country ||
          accountInfo?.country ||
          'US',
      })),
  )
}

// Scheduling step's Back button. Mirrors the legacy onboarding page's back
// arrow (NewOrderLayout + ConfirmGoBackModal): with nothing booked it just
// returns to the previous step; with any call booked it asks first, and on
// Confirm releases every booked call (DELETE the slot, mark the
// scheduled_appointment Cancelled) before navigating — so the rep has to
// reschedule. PartnerOps skips the cancellations and only navigates, same as
// legacy. If a slot can't be released the rep stays on this step to retry.
//
// appointmentsByLocationId/locations/accountInfo: what to cancel
// onCancelFailed: called after a failed attempt so the parent can refetch
//   and a retry only re-attempts the calls still booked
export function SchedulingBackButton({
  appointmentsByLocationId,
  locations,
  accountInfo,
  onCancelFailed,
}) {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const effectiveRole = useSelector(selectEffectiveRole)
  const isPartnerOps = (
    Array.isArray(effectiveRole) ? effectiveRole : [effectiveRole]
  ).some(role => String(role).toLowerCase() === 'partnerops')

  const [showConfirm, setShowConfirm] = useState(false)
  const [goingBack, setGoingBack] = useState(false)

  const bookedAppointments = getBookedAppointments({
    appointmentsByLocationId,
    locations,
    accountInfo,
  })

  const handleBack = () => {
    if (bookedAppointments.length) {
      setShowConfirm(true)
      return
    }
    dispatch(goBack())
  }

  const handleConfirm = async () => {
    setGoingBack(true)
    if (!isPartnerOps) {
      let hadFailure = false
      for (const {appointment, callType, country} of bookedAppointments) {
        if (!appointment?.appt_id || !callType) continue
        try {
          await client(callType.cancelEndpoint, {
            data: {
              type: callType.appointmentTypeKey,
              demoApptId: appointment.appt_id,
              country,
            },
            method: 'DELETE',
          })
        } catch {
          // Slot is still held on the backend — leave its row as-is so a
          // retry picks it up, and keep the rep on this step.
          hadFailure = true
          toast.error(`Error cancelling ${callType.label}. Please try again.`)
          continue
        }
        try {
          await client(
            'ordercapture/services/updateScheduledAppointmentStatus',
            {
              data: {
                appt_id: appointment.appt_id,
                schedule_status: 'Cancelled',
              },
              method: 'PUT',
            },
          )
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Unable to mark appointment cancelled', err)
        }
      }
      if (hadFailure) {
        onCancelFailed?.()
        setGoingBack(false)
        return
      }
    }
    setGoingBack(false)
    setShowConfirm(false)
    dispatch(goBack())
  }

  return (
    <>
      {/* Left half of the Back | Cancel joined pill — same look as the
          Proposal Tool's schedule-appointment page (ClientProposalSchedule's
          ActionLeft); the footer supplies the Cancel half. */}
      <Button
        variant="secondary-gray"
        size="md"
        type="button"
        disabled={goingBack}
        onClick={handleBack}
        leftIcon={<PTArrowLeftSVG width="14" height="14" />}
        style={{borderRadius: '80px 0 0 80px'}}
      >
        Back
      </Button>

      <Modal
        isOpen={showConfirm}
        onClose={() => !goingBack && setShowConfirm(false)}
        title="Are you sure you want to go back?"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary-gray"
              size="sm"
              disabled={goingBack}
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="solid-gradiant"
              size="sm"
              disabled={goingBack}
              onClick={handleConfirm}
            >
              {goingBack ? 'Cancelling…' : 'Confirm'}
            </Button>
          </>
        }
      >
        If you go back, you will have to reschedule the appointment.
      </Modal>
    </>
  )
}
